import { LANG_NAMES } from '../../constants';
import { langName } from '../../functions';
import type { JobPostingData } from '../../../../../utils/types/JobOpenings';

export interface IShareContent {
  shareText: string;
  emailSubject: string;
  emailBody: string;
}

export const buildShareContent = (
  jobTitle: string,
  companyName: string,
  jobPostUrl: string,
): IShareContent => {
  const safeCompany = companyName || 'Company';
  const safeTitle = jobTitle || 'Job Opportunity';
  return {
    shareText: `Check out this job: ${safeTitle} at ${safeCompany}\n\nApply here: ${jobPostUrl}`,
    emailSubject: `Job Opportunity: ${safeTitle} at ${safeCompany}`,
    emailBody:
      `Hey,\n\nI found this job opportunity and thought of you:\n\nPosition: ${safeTitle}\nCompany: ${safeCompany}\nApply here: ${jobPostUrl}\n\nThis looks like it could be a great fit for your skills!\n\nBest regards`,
  };
};

export const buildMailtoUrl = (subject: string, body: string): string => {
  if (!subject && !body) return '';
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const buildWhatsAppUrl = (shareText: string): string =>
  `https://wa.me/?text=${encodeURIComponent(shareText)}`;

export const buildFacebookUrl = (shareText: string, jobPostUrl: string): string =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobPostUrl)}&quote=${encodeURIComponent(shareText)}`;

export const buildLinkedInUrl = (subject: string, shareText: string, jobPostUrl: string): string =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobPostUrl)}&title=${encodeURIComponent(subject)}&summary=${encodeURIComponent(`${shareText.substring(0, 200)}...`)}`;

export const buildXUrl = (shareText: string, jobPostUrl: string): string =>
  `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareText.substring(0, 240)}...`)}&url=${encodeURIComponent(jobPostUrl)}`;

export const buildJobPostUrl = (origin: string, jobPostId?: string): string =>
  jobPostId ? `${origin}/jobs/${jobPostId}` : '';

export const getContentLangName = (code: string): string => langName(code, LANG_NAMES);

export const resolveContentLang = (
  availableLanguages: string[] | undefined,
  uiLanguage: string | undefined,
): string | null => {
  const available = availableLanguages ?? [];
  if (available.length === 0) return null;
  const uiLang = uiLanguage?.split('-')[0] ?? 'en';
  const target = uiLang === 'en' ? 'en' : available.includes(uiLang) ? uiLang : 'en';
  return target;
};

export const getAvailableContentLanguages = (jobPostData?: JobPostingData): string[] => {
  if (!jobPostData?.availableLanguages || jobPostData.availableLanguages.length < 1) return [];
  return ['en', ...jobPostData.availableLanguages.filter((lang) => lang !== 'en')];
};
